import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { FileText, RefreshCw, Download } from 'lucide-react';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useAudit } from '@/hooks/useAudit';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { AuditEntry } from '@/api/audit.api';

export const Route = createFileRoute('/_authenticated/auditoria')({
  component: AuditoriaPage,
});

const ACTION_LABELS: Record<string, string> = {
  deposit_confirmed: 'Depósito confirmado',
  deposit_rejected: 'Depósito rechazado',
  deposit_pending: 'Depósito pendiente',
  withdrawal_confirmed: 'Retiro confirmado',
  withdrawal_rejected: 'Retiro rechazado',
  withdrawal_pending: 'Retiro pendiente',
  bonus_confirmed: 'Bono confirmado',
  adjustment_confirmed: 'Ajuste confirmado',
};

const ACTION_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'success' | 'warning'> = {
  deposit_confirmed: 'success',
  deposit_rejected: 'destructive',
  deposit_pending: 'warning',
  withdrawal_confirmed: 'success',
  withdrawal_rejected: 'destructive',
  withdrawal_pending: 'warning',
  bonus_confirmed: 'default',
  adjustment_confirmed: 'secondary',
};

function AuditoriaPage() {
  const [actionFilter, setActionFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const params = useMemo(() => ({
    ...(actionFilter ? { action: actionFilter } : {}),
    ...(fromDate ? { fromDate: new Date(fromDate).toISOString() } : {}),
    ...(toDate ? { toDate: new Date(toDate).toISOString() } : {}),
    limit: 100,
  }), [actionFilter, fromDate, toDate]);

  const { data: entries = [], isLoading, refetch } = useAudit(params);

  const columns = useMemo<ColumnDef<AuditEntry>[]>(
    () => [
      {
        accessorKey: 'timestamp',
        header: 'Fecha',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {formatDate(row.original.timestamp)}
          </span>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Acción',
        cell: ({ row }) => {
          const label = ACTION_LABELS[row.original.action] || row.original.action;
          const variant = ACTION_VARIANTS[row.original.action] || 'secondary';
          return <Badge variant={variant}>{label}</Badge>;
        },
      },
      {
        accessorKey: 'actor',
        header: 'Operador',
        cell: ({ row }) => (
          <span className="font-medium">{row.original.actor}</span>
        ),
      },
      {
        accessorKey: 'clientName',
        header: 'Cliente',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.clientName || '—'}</span>
            {row.original.casinoUser && (
              <span className="text-xs text-muted-foreground">{row.original.casinoUser}</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'amount',
        header: 'Monto',
        cell: ({ row }) => (
          <span className="font-mono font-medium">
            {formatCurrency(row.original.amount)}
          </span>
        ),
      },
      {
        accessorKey: 'paymentType',
        header: 'Tipo',
        cell: ({ row }) => (
          <span className="text-sm capitalize">{row.original.paymentType}</span>
        ),
      },
      {
        accessorKey: 'detail',
        header: 'Detalle',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground max-w-[200px] truncate">
            {row.original.detail || '—'}
          </span>
        ),
      },
    ],
    []
  );

  const handleExport = async () => {
    try {
      const { auditApi } = await import('@/api/audit.api');
      const csv = await auditApi.exportCsv(params);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auditoria-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // toast handled by api error interceptor
    }
  };

  return (
    <>
      <TopBanner title="Auditoría" subtitle="Historial de acciones y pagos" />
      <div className="flex-1 overflow-auto p-6">
        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4 mb-6">
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-primary/10 p-2.5">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold">{entries.length}</div>
                <div className="text-xs text-muted-foreground">Registros</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-green-100 p-2.5">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {entries.filter((e) => e.action.includes('confirmed')).length}
                </div>
                <div className="text-xs text-muted-foreground">Confirmados</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-red-100 p-2.5">
                <FileText className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {entries.filter((e) => e.action.includes('rejected')).length}
                </div>
                <div className="text-xs text-muted-foreground">Rechazados</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-amber-100 p-2.5">
                <FileText className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {entries.filter((e) => e.action.includes('pending')).length}
                </div>
                <div className="text-xs text-muted-foreground">Pendientes</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Input
            placeholder="Filtrar por acción..."
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-48"
          />
          <Input
            type="date"
            placeholder="Desde"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-40"
          />
          <Input
            type="date"
            placeholder="Hasta"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-40"
          />
          <Button variant="outline" size="sm" onClick={() => { setActionFilter(''); setFromDate(''); setToDate(''); }}>
            Limpiar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            onClick={() => refetch()}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Actualizar
          </Button>
          <Button size="sm" variant="outline" onClick={handleExport}>
            <Download className="h-3.5 w-3.5 mr-1" />
            Exportar CSV
          </Button>
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={entries}
          isLoading={isLoading}
          emptyMessage="No hay registros de auditoría"
        />
      </div>
    </>
  );
}
